import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { SiGraphql } from 'react-icons/si';
import SwitchButton3 from '../../../componentes/SwitchButton3';
import axios from '../../../utils/axiosConfig';
import FormModalCreateAlerta from '../forms/formModalCreateAlerta';

const ALERT_ICONS = [
    { base: 'Apertura', file: 'candado.png' },
    { base: 'Bateria', file: 'batt-charge.png' },
    { base: 'ZonaSegura', file: 'secured.png' },
    { base: 'Geocercas', component: <SiGraphql size={20} /> },
    { base: 'Desvio', file: 'desvioo-Photoroom.png' },
    { base: 'Retroceso', file: 'back.png' },
    { base: 'Luz', file: 'ligth.png' },
    { base: 'GpsEstatico', file: 'NoGPS.png' },
    { base: 'Velocidad', file: 'moving.png' },
    { base: 'TiempoGps', file: 'timer-regular-24.png' },
];

const getAlertKey = (item, index) =>
    item?.ID || item?.IdContacto || item?.NombreContacto || `${item?.Mail || 'alerta'}-${index}`;

const normalizeAlertas = (rows) => (
    Array.isArray(rows)
        ? rows.map((item, index) => ({
            ...item,
            __rowKey: getAlertKey(item, index),
            __searchText: [
                item?.NombreContacto,
                item?.Mail,
                item?.Telefono,
                item?.RolTrafico,
                item?.NombreEmpresa,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase(),
        }))
        : []
);

const AlertIconCell = memo(function AlertIconCell({ item, base, file, component }) {
    const enabled = Boolean(item?.[`${base}M`]) && Boolean(item?.[`${base}T`]);

    return (
        <td style={{ textAlign: 'center' }}>
            {component ? (
                <span
                    style={{
                        filter: enabled ? 'none' : 'grayscale(100%)',
                        opacity: enabled ? 1 : 0.4,
                    }}
                >
                    {component}
                </span>
            ) : (
                <img
                    src={`/images/${file}`}
                    alt={base}
                    loading="lazy"
                    decoding="async"
                    style={{
                        width: 20,
                        height: 20,
                        filter: enabled ? 'none' : 'grayscale(100%)',
                        opacity: enabled ? 1 : 0.4,
                    }}
                />
            )}
        </td>
    );
});

const AlertFlagCell = memo(function AlertFlagCell({ enabled, label }) {
    return (
        <td style={{ textAlign: 'center' }}>
            <span
                style={{
                    color: enabled ? '#000' : '#bfc5cc',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    lineHeight: 1,
                    display: 'inline-block',
                    minWidth: 16,
                }}
                title={label}
            >
                {label}
            </span>
        </td>
    );
});

const AlertRow = memo(function AlertRow({ item, canEdit, onEdit }) {
    return (
        <tr>
            <td
                style={{
                    color: '#000',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                }}
            >
                {item.NombreContacto || '-'}
            </td>

            {ALERT_ICONS.map((config) => (
                <AlertIconCell
                    key={config.base}
                    item={item}
                    base={config.base}
                    file={config.file}
                    component={config.component}
                />
            ))}

            <AlertFlagCell
                enabled={Boolean(item?.ReportTrafico)}
                label="R"
            />
            <AlertFlagCell
                enabled={Boolean(item?.AutoReporte ?? item?.Autoreporte)}
                label="A"
            />

            <td style={{ textAlign: 'center' }}>
                <button
                    type="button"
                    className="btn btn-link p-0 border-0"
                    onClick={() => canEdit && onEdit(item)}
                    disabled={!canEdit}
                    style={{ textDecoration: 'none' }}
                >
                    <i className="bx bxs-pencil bx-tada" style={{ color: '#006699' }} />
                </button>
            </td>
        </tr>
    );
});

export default function ModalAlertas({
    bitInserts,
    bitEdits,
    bitDeletes,
    isOpen = false,
}) {
    const [tipoMail, setTipoMail] = useState('usuarios');
    const [view, setView] = useState('list');
    const [searchTerm, setSearchTerm] = useState('');
    const [alertas, setAlertas] = useState([]);
    const [selectedAlerta, setSelectedAlerta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const deferredSearchTerm = useDeferredValue(searchTerm.trim().toLowerCase());

    const filteredAlertas = useMemo(() => {
        if (!deferredSearchTerm) return alertas;
        return alertas.filter((item) => item.__searchText.includes(deferredSearchTerm));
    }, [alertas, deferredSearchTerm]);

    const refreshAlertas = useCallback(() => {
        setRefreshKey((current) => current + 1);
    }, []);

    const openCreateForm = useCallback(() => {
        setSelectedAlerta(null);
        setView('form');
    }, []);

    const openEditForm = useCallback((item) => {
        setSelectedAlerta(item);
        setView('form');
    }, []);

    const closeForm = useCallback(() => {
        setSelectedAlerta(null);
        setView('list');
        refreshAlertas();
    }, [refreshAlertas]);

    useEffect(() => {
        if (!isOpen) {
            setView('list');
            setSearchTerm('');
            setSelectedAlerta(null);
            return;
        }

        let active = true;
        const controller = new AbortController();

        const loadAlertas = async () => {
            setLoading(true);

            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_SERVER}/operaciones2/getcontactos`,
                    JSON.stringify({ tipo: tipoMail === 'usuarios' ? 0 : 1 }),
                    {
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                        },
                        withCredentials: true,
                    }
                );

                if (!active) return;
                const rows = response?.data?.data?.recordset || [];
                console.log('Alertas obtenidas:', rows);
                setAlertas(normalizeAlertas(rows));
            } catch (error) {
                if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
                console.error('Error fetching alertas:', error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadAlertas();

        return () => {
            active = false;
            controller.abort();
        };
    }, [isOpen, tipoMail, refreshKey]);

    return (
        <>
            <style>
                {`
                    @media (max-width: 575.98px) {
                        .btn-nueva-alerta {
                            width: 100%;
                        }
                    }
                `}
            </style>

            {view === 'form' ? (
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                {selectedAlerta ? 'Editar Alerta' : 'Crear Alerta'}
                            </h5>
                            <button
                                type="button"
                                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                                onClick={closeForm}
                            >
                                <i className="bx bx-arrow-back" aria-hidden="true" />
                                Volver
                            </button>
                        </div>

                        <FormModalCreateAlerta
                            onClose={closeForm}
                            selectedAlerta={selectedAlerta}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <Row className="mb-4 align-items-center">
                        <Col sm="6">
                            <input
                                type="text"
                                className="form-input w-100"
                                placeholder="Buscar alertas..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                style={{ height: '38px', boxSizing: 'border-box' }}
                            />
                        </Col>

                        <Col
                            sm="4"
                            className="d-flex justify-content-center justify-content-sm-end align-items-center gap-3 mt-2 mt-sm-0"
                        >
                            <SwitchButton3
                                checked={tipoMail === 'usuarios'}
                                onChange={(active) => active && setTipoMail('usuarios')}
                                type="Contacto Usuarios"
                                disabled={loading}
                            />
                            <SwitchButton3
                                checked={tipoMail === 'proyectos'}
                                onChange={(active) => active && setTipoMail('proyectos')}
                                type="Contacto Proyectos"
                                disabled={loading}
                            />
                        </Col>

                        <Col
                            sm="2"
                            className="d-flex justify-content-center justify-content-sm-end mt-2 mt-sm-0"
                        >
                            <button
                                className="Button blue btn-nueva-alerta"
                                onClick={() => bitInserts && openCreateForm()}
                                style={{ height: '38px', minWidth: '150px' }}
                                disabled={loading && !alertas.length}
                            >
                                Nueva Alerta
                            </button>
                        </Col>
                    </Row>

                    <div className="card ContainerTableMaestros">
                        <div
                            className="table-responsive"
                            style={{ height: '300px', overflowY: 'auto', contain: 'content' }}
                        >
                            <table
                                className="table table-hover"
                                id="tabla"
                                style={{ width: '100%', overflowX: 'auto', tableLayout: 'fixed' }}
                            >
                                <thead className="FondoHeaderTabla2">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="text-white text-center"
                                            style={{ width: '30%' }}
                                        >
                                            Nombre
                                        </th>

                                        {ALERT_ICONS.map((config) => (
                                            <th
                                                key={config.base}
                                                scope="col"
                                                style={{ width: '6%' }}
                                            />
                                        ))}

                                        <th
                                            scope="col"
                                            className="text-white text-center"
                                            style={{ width: '5%' }}
                                            title="Rep. Tráfico"
                                        >
                                            
                                        </th>
                                        <th
                                            scope="col"
                                            className="text-white text-center"
                                            style={{ width: '5%' }}
                                            title="Autoreporte"
                                        >
                                            
                                        </th>

                                        <th
                                            scope="col"
                                            className="text-white text-center"
                                            style={{ width: '15%' }}
                                        >
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={ALERT_ICONS.length + 2} className="text-center py-4">
                                                Cargando alertas...
                                            </td>
                                        </tr>
                                    ) : filteredAlertas.length === 0 ? (
                                        <tr>
                                            <td colSpan={ALERT_ICONS.length + 2} className="text-center py-4">
                                                No se encontraron alertas.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredAlertas.map((item) => (
                                            <AlertRow
                                                key={item.__rowKey}
                                                item={item}
                                                canEdit={Boolean(bitEdits)}
                                                onEdit={openEditForm}
                                            />
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
